import React, { FunctionComponent, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { Modal, Header } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { compose } from 'recompose';
import classnames from 'classnames';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import dateFnsFormat from 'date-fns/format';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import InputMask from 'react-input-mask';
import moment from 'moment';

import { WithRouterProps, ClassProp } from 'helpers/types';
import { useMutation } from 'helpers/useMutation';
import Button from 'components/Button';
import GET_MERCHANT_OFFERS_RESULTS from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffers.queries';

import styles from './EndOfferModal.styles';
import END_OFFER_FOR_MERCHANT from './EndOfferModal.mutations';

type Styles = ClassProp<ReturnType<typeof styles>>;

type FormData = {
  endDateDay: Date | string;
  endDateTime: string;
};

type Props = WithRouterProps & {
  classes: Styles;
  trigger: any;
  title: string;
  offerUuid: string;
};

const formatDate = (date: Date, format: string) => dateFnsFormat(date, format);

const formatEndDate = (day: Date | string, time: string) =>
  `${moment(day).format('YYYY-MM-DD')}T${time}`;

const EndOfferModal: FunctionComponent<Props> = ({
  match,
  trigger,
  title,
  classes,
  offerUuid,
}) => {
  const { merchantId } = match.params as { merchantId: string };
  const { runMutation, loading } = useMutation(
    END_OFFER_FOR_MERCHANT,
    {},
    'The offer has been successfully terminated.',
  );

  const {
    register,
    setValue,
    handleSubmit,
    errors,
    trigger: triggerValidation,
  } = useForm<FormData>({
    defaultValues: {
      endDateDay: moment(Date.now()).format('MM/DD/YYYY'),
      endDateTime: '23:59:00',
    },
  });

  const onSubmit = (data: FormData) => {
    runMutation({
      variables: {
        params: {
          offerUuid,
          merchantUuid: merchantId,
          endDate: formatEndDate(data.endDateDay, data.endDateTime),
        },
      },

      refetchQueries: [
        {
          query: GET_MERCHANT_OFFERS_RESULTS,
          variables: { params: { merchantId } },
        },
      ],
    });
  };

  useEffect(() => {
    register(
      { name: 'endDateDay' },
      { required: 'The end date day is required.' },
    );

    register(
      { name: 'endDateTime' },
      { required: 'The end date time is required.' },
    );
  }, [register]);

  const endDateOnChange = (day: Date) => {
    setValue('endDateDay', day);
    triggerValidation('endDateDay');
  };

  const endDateTimeOnChange = async (event: any) => {
    const { value } = event.target;
    setValue('endDateTime', value);
    triggerValidation('endDateTime');
  };

  return (
    <Modal trigger={trigger} closeIcon size="mini">
      <Header className={classes.header} icon="upload" content={title} />

      <Modal.Content>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.fieldGroup}>
            <label className={classes.label}>End Date (Day):</label>

            <DayPickerInput
              onDayChange={endDateOnChange}
              formatDate={formatDate}
              format="MM-dd-yyyy"
              dayPickerProps={{ className: 'selectable' }}
              placeholder={`${dateFnsFormat(new Date(), 'MM/dd/yyyy')}`}
            />

            <ErrorMessage
              errors={errors}
              name="endDateDay"
              render={({ message }) => (
                <span className={classes.message}>{message}</span>
              )}
            ></ErrorMessage>
          </div>

          <div className={classes.fieldGroup}>
            <label className={classes.label}>End Date Time (UTC):</label>

            <InputMask
              onChange={endDateTimeOnChange}
              name="endDateTime"
              className={classes.input}
              mask="99:99:99"
              defaultValue="23:59:00"
            />

            <ErrorMessage
              errors={errors}
              name="endDateTime"
              render={({ message }) => (
                <span className={classes.message}>{message}</span>
              )}
            ></ErrorMessage>
          </div>

          <div className={classes.fieldGroup}>
            <Button
              type="submit"
              title={loading ? 'Loading...' : 'Submit'}
              className={classnames({
                [classes.disabled]: loading,
              })}
            />
          </div>
        </form>
      </Modal.Content>
    </Modal>
  );
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(EndOfferModal);
